import * as React from "react";
import {
  ComponentBase,
  ComponentBaseProps,
  ComponentBaseState,
} from "../ComponentBase";
import {
  getIsLoadingPDPPointsTotal,
  getPDPPointsTotal,
} from "../../reducers/loyalty/methods";
import { interpolate } from "../../utils/string";
import "./PDPPoints.css";

interface DerivedComponentState extends ComponentBaseState {
  totalPoints: number;
}

export interface PDPPointsComponentProps extends ComponentBaseProps {
  classNames: string;
  label: string;
  style: { [styleProp: string]: string };
  isLoading: ReturnType<typeof getIsLoadingPDPPointsTotal>;
  pdpPointsTotal: ReturnType<typeof getPDPPointsTotal>;
  templateContent: string;
  defaultTemplateContent: string;
  errorStyle: { [styleProp: string]: string };
  editStatus: "success" | "error" | "loading";
}

export class PDPPoints extends ComponentBase<PDPPointsComponentProps> {
  state: DerivedComponentState = {
    ...this.state,
    totalPoints: -1,
  };

  constructor(props: PDPPointsComponentProps) {
    super(props);
  }

  calculateAndSetTotalPoints = () => {
    // check if points total is already set
    if (this.state.totalPoints !== -1) {
      return;
    }

    // If we've got a valid response, calculate the total points that
    // are of type "ledger" and have a currency matching the loyalty currency
    if (
      this.props.pdpPointsTotal &&
      this.props.pdpPointsTotal.loyaltyCurrency
    ) {
      const { loyaltyCurrency, potentialRewards, error } =
        this.props.pdpPointsTotal;

      if (error) {
        return;
      }

      const totalPoints = potentialRewards
        .filter((reward) => reward !== null) // filter out null rewards
        .filter(
          (reward) =>
            reward.rewardType === "ledger" &&
            reward.currency === loyaltyCurrency &&
            reward.amount > 0
        )
        .reduce((acc, reward) => acc + reward.amount, 0);

      // Cast to any because setState in constructor is not officially supported by React
      // but we need to initialize state with calculated points value
      this.setState({ totalPoints } as any);
    }
  };

  componentDidUpdate(prevProps: PDPPointsComponentProps) {
    super.componentDidUpdate(prevProps);
    this.calculateAndSetTotalPoints();
  }

  getEditContent = () => {
    return this.props.templateContent || this.props.defaultTemplateContent;
  };

  getTextContent = () => {
    const {
      isLoading,
      isEditing,
      pdpPointsTotal,
      templateContent,
      defaultTemplateContent,
    } = this.props;

    const { totalPoints } = this.state;

    if (isEditing) {
      return this.getEditContent();
    }

    if (pdpPointsTotal && pdpPointsTotal.error) {
      return <div>You'll earn points on this purchase.</div>;
    }

    if (isLoading || !pdpPointsTotal || !pdpPointsTotal.loyaltyCurrency) {
      return <div className="skeleton-loader" />;
    }

    return interpolate(
      templateContent || defaultTemplateContent,
      {
        points: totalPoints,
      },
      true
    );
  };

  render() {
    return (
      <div
        id={this.props.configName}
        style={{ ...this.props.style }}
        onDragStart={this.onDragStart}
        onMouseDown={this.onMouseDown}
        className={`PDPPoints-container`}
        aria-label={this.props.label}
        ref={(component) => (this.component = component)}
      >
        <span>{this.getTextContent()}</span>
      </div>
    );
  }
}
